import axios from "axios";
import i18next from "i18next";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useTenant } from "../hooks";

const CurrencyContext = createContext();

const CurrencyReducer = (state, action) => {
  switch (action.type) {
    case "SET_CURRENCIES":
      return {
        ...state,
        currencies: action.payload,
      };
    default:
      return state;
  }
};

const extractCurrencyList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.currencies)) return payload.currencies;
  if (Array.isArray(payload?.data?.currencies)) return payload.data.currencies;
  return [];
};

const normalizeCurrencyId = (value) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric) && String(value || "").trim() !== "") {
    return numeric;
  }
  return value ?? null;
};

const resolveCurrencyCode = (currency) => {
  if (currency?.code) return currency.code;
  if (currency?.currency_code) return currency.currency_code;
  if (currency?.currency) return currency.currency;
  if (currency?.abbr) return currency.abbr;
  return "";
};

const resolveCurrencyLabel = (currency, isArabic, fallbackCode) => {
  if (isArabic) {
    return (
      currency?.name_ar ||
      currency?.name ||
      currency?.name_en ||
      fallbackCode ||
      ""
    );
  }
  return (
    currency?.name_en ||
    currency?.name ||
    currency?.name_ar ||
    fallbackCode ||
    ""
  );
};

const normalizeCurrencyOption = (currency, lang) => {
  const id = normalizeCurrencyId(
    currency?.id ??
      currency?._id ??
      currency?.currency_id ??
      currency?.currencyId,
  );
  const code = String(resolveCurrencyCode(currency) || "").toUpperCase();
  const label = resolveCurrencyLabel(currency, lang === "ar", code);
  return {
    ...currency,
    id,
    value: id,
    code,
    label,
  };
};

const normalizeCurrencyList = (currencies, lang) => {
  if (!Array.isArray(currencies)) return [];
  return currencies
    .map((currency) => normalizeCurrencyOption(currency, lang))
    .filter((currency) => currency.id !== null && currency.id !== undefined);
};

export const CurrencyProvider = ({ children }) => {
  const baseUrl = process.env.REACT_APP_INV_API_SERVER_CURRENCY_BASE_URL;
  const { tenantId, companySelected } = useTenant();
  const [loading, setLoading] = useState(false);
  const [state, dispatch] = useReducer(CurrencyReducer, {
    currencies: [],
  });

  const fetchCurrencies = useCallback(async () => {
    const companyId = companySelected?.value;

    if (!baseUrl || !tenantId || !companyId) {
      dispatch({ type: "SET_CURRENCIES", payload: [] });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/${tenantId}/${companyId}`);

      const list = extractCurrencyList(response?.data);
      dispatch({ type: "SET_CURRENCIES", payload: list });
    } catch (error) {
      console.error(
        "Error fetching currencies:",
        error?.response?.data || error?.message || error,
      );
      dispatch({ type: "SET_CURRENCIES", payload: [] });
    } finally {
      setLoading(false);
    }
  }, [baseUrl, tenantId, companySelected?.value]);

 useEffect(() => {
  fetchCurrencies();
}, [tenantId, companySelected?.value]);

  const lang = i18next.language === "ar" ? "ar" : "en";
  const currencies = useMemo(
    () => normalizeCurrencyList(state.currencies, lang),
    [state.currencies, lang],
  );

  const value = useMemo(
    () => ({
      currencies,
      loading,
      refreshCurrencies: fetchCurrencies,
    }),
    [currencies, loading, fetchCurrencies],
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
